import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { database } from '../config/firebase';
import './WallLayoutEditor.css';

const WallLayoutEditor = () => {
  const [layouts, setLayouts] = useState({
    painting: { gridColumn: '2 / 6', gridRow: '2 / 6' },
    omiClock: { gridColumn: '7 / 9', gridRow: '2 / 4' },
    henryClock: { gridColumn: '10 / 12', gridRow: '2 / 4' },
    calendar: { gridColumn: '10 / 12', gridRow: '4 / 7' },
    frame: { gridColumn: '2 / 5', gridRow: '6 / 9' },
    telescope: { gridColumn: '10 / 12', gridRow: '6 / 8' },
    yoSign: { gridColumn: '11 / 13', gridRow: '7 / 8' }
  });

  const [selectedItem, setSelectedItem] = useState(null);
  const [draggingItem, setDraggingItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const gridRef = useRef(null);

  // Load layouts from Firebase
  useEffect(() => {
    const layoutsRef = ref(database, 'wallLayouts/default');

    const unsubscribe = onValue(layoutsRef, (snapshot) => {
      if (snapshot.exists()) {
        setLayouts(snapshot.val());
      }
    });

    return () => unsubscribe();
  }, []);

  // Parse grid string to numbers
  const parseGridValue = (gridStr) => {
    const parts = gridStr.split('/').map(s => parseInt(s.trim()));
    return { start: parts[0], end: parts[1] };
  };

  // Convert grid numbers to string
  const gridToString = (start, end) => {
    return `${start} / ${end}`;
  };

  // Handle drag start
  const handleDragStart = (e, itemId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
    setDraggingItem(itemId);
    setSelectedItem(itemId);
  };

  // Handle drag over grid cell
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop on grid cell or item
  const handleDrop = (e, colStart, rowStart) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggingItem) return;

    // If colStart and rowStart are provided, use them
    // Otherwise, calculate from mouse position
    let targetCol = colStart;
    let targetRow = rowStart;

    if (!targetCol || !targetRow) {
      if (!gridRef.current) {
        console.error('Grid ref not available');
        setDraggingItem(null);
        return;
      }

      // Calculate grid position from mouse coordinates
      const gridRect = gridRef.current.getBoundingClientRect();
      const x = e.clientX - gridRect.left;
      const y = e.clientY - gridRect.top;

      const cellWidth = gridRect.width / 12;
      const cellHeight = gridRect.height / 8;

      targetCol = Math.floor(x / cellWidth) + 1;
      targetRow = Math.floor(y / cellHeight) + 1;

      // Clamp to valid range
      targetCol = Math.max(1, Math.min(12, targetCol));
      targetRow = Math.max(1, Math.min(8, targetRow));

      console.log('Drop at:', { targetCol, targetRow, x, y, cellWidth, cellHeight });
    }

    const currentLayout = layouts[draggingItem];
    const currentCol = parseGridValue(currentLayout.gridColumn);
    const currentRow = parseGridValue(currentLayout.gridRow);

    // Calculate the span (how many cells the item occupies)
    const colSpan = currentCol.end - currentCol.start;
    const rowSpan = currentRow.end - currentRow.start;

    // Calculate new end positions
    const newColEnd = Math.min(targetCol + colSpan, 13);
    const newRowEnd = Math.min(targetRow + rowSpan, 9);

    console.log('Moving', draggingItem, 'to', { targetCol, targetRow, newColEnd, newRowEnd });

    // Update layout
    setLayouts(prev => ({
      ...prev,
      [draggingItem]: {
        gridColumn: gridToString(targetCol, newColEnd),
        gridRow: gridToString(targetRow, newRowEnd)
      }
    }));

    setDraggingItem(null);
  };

  // Handle resize
  const handleResize = (itemId, direction, delta) => {
    const currentLayout = layouts[itemId];
    const col = parseGridValue(currentLayout.gridColumn);
    const row = parseGridValue(currentLayout.gridRow);

    let newCol = { ...col };
    let newRow = { ...row };

    switch (direction) {
      case 'right':
        newCol.end = Math.min(Math.max(col.end + delta, col.start + 1), 13);
        break;
      case 'left':
        newCol.start = Math.max(Math.min(col.start + delta, col.end - 1), 1);
        break;
      case 'bottom':
        newRow.end = Math.min(Math.max(row.end + delta, row.start + 1), 9);
        break;
      case 'top':
        newRow.start = Math.max(Math.min(row.start + delta, row.end - 1), 1);
        break;
      default:
        break;
    }

    setLayouts(prev => ({
      ...prev,
      [itemId]: {
        gridColumn: gridToString(newCol.start, newCol.end),
        gridRow: gridToString(newRow.start, newRow.end)
      }
    }));
  };

  const saveLayout = async () => {
    setIsSaving(true);
    setMessage('');

    try {
      const layoutsRef = ref(database, 'wallLayouts/default');
      await set(layoutsRef, layouts);
      setMessage('✓ Layout saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving layout:', error);
      setMessage('✗ Error saving layout');
    } finally {
      setIsSaving(false);
    }
  };

  const resetToDefault = async () => {
    const defaultLayouts = {
      painting: { gridColumn: '2 / 6', gridRow: '2 / 6' },
      omiClock: { gridColumn: '7 / 9', gridRow: '2 / 4' },
      henryClock: { gridColumn: '10 / 12', gridRow: '2 / 4' },
      calendar: { gridColumn: '10 / 12', gridRow: '4 / 7' },
      frame: { gridColumn: '2 / 5', gridRow: '6 / 9' },
      telescope: { gridColumn: '10 / 12', gridRow: '6 / 8' },
      yoSign: { gridColumn: '11 / 13', gridRow: '7 / 8' }
    };

    setLayouts(defaultLayouts);

    try {
      const layoutsRef = ref(database, 'wallLayouts/default');
      await set(layoutsRef, defaultLayouts);
      setMessage('✓ Reset to default layout');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error resetting layout:', error);
      setMessage('✗ Error resetting layout');
    }
  };

  const wallItems = [
    { id: 'painting', name: 'Trail Painting', icon: '🖼️', color: '#d9a5a5' },
    { id: 'omiClock', name: "Omi's Clock", icon: '🕰️', color: '#a5c9d9' },
    { id: 'henryClock', name: "Henry's Clock", icon: '🕰️', color: '#c9a5d9' },
    { id: 'calendar', name: 'Days Calendar', icon: '📅', color: '#d9c9a5' },
    { id: 'frame', name: 'Picture Frame', icon: '📷', color: '#a5d9c9' },
    { id: 'telescope', name: 'Telescope', icon: '🔭', color: '#c9d9a5' },
    { id: 'yoSign', name: 'YO Sign', icon: '✍️', color: '#d9a5c9' }
  ];

  // Render grid cells
  const renderGrid = () => {
    const cells = [];
    for (let row = 1; row <= 8; row++) {
      for (let col = 1; col <= 12; col++) {
        cells.push(
          <div
            key={`${col}-${row}`}
            className="grid-cell"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, col, row)}
            data-col={col}
            data-row={row}
          >
            <span className="cell-label">{col},{row}</span>
          </div>
        );
      }
    }
    return cells;
  };

  // Render draggable items on the grid
  const renderGridItems = () => {
    return wallItems.map(item => {
      const layout = layouts[item.id];
      if (!layout) return null;

      const isSelected = selectedItem === item.id;

      return (
        <div
          key={item.id}
          className={`grid-item ${isSelected ? 'selected' : ''} ${draggingItem === item.id ? 'dragging' : ''}`}
          style={{
            gridColumn: layout.gridColumn,
            gridRow: layout.gridRow,
            backgroundColor: item.color
          }}
          draggable
          onDragStart={(e) => handleDragStart(e, item.id)}
          onClick={() => setSelectedItem(item.id)}
        >
          <div className="grid-item-content">
            <span className="grid-item-icon">{item.icon}</span>
            <span className="grid-item-name">{item.name}</span>
          </div>

          {isSelected && (
            <>
              <button
                className="resize-handle resize-right"
                onClick={(e) => { e.stopPropagation(); handleResize(item.id, 'right', 1); }}
                title="Expand right"
              >
                →
              </button>
              <button
                className="resize-handle resize-left"
                onClick={(e) => { e.stopPropagation(); handleResize(item.id, 'left', -1); }}
                title="Expand left"
              >
                ←
              </button>
              <button
                className="resize-handle resize-bottom"
                onClick={(e) => { e.stopPropagation(); handleResize(item.id, 'bottom', 1); }}
                title="Expand down"
              >
                ↓
              </button>
              <button
                className="resize-handle resize-top"
                onClick={(e) => { e.stopPropagation(); handleResize(item.id, 'top', -1); }}
                title="Expand up"
              >
                ↑
              </button>
            </>
          )}
        </div>
      );
    });
  };

  return (
    <div className="wall-layout-editor">
      <div className="editor-header">
        <h2>Wall Layout Editor</h2>
        <p className="editor-description">
          Drag items to reposition them on the grid. Click an item to select it and use the arrows to resize.
        </p>
      </div>

      <div className="visual-grid-container">
        <div
          className="visual-grid"
          ref={gridRef}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e)}
        >
          {renderGrid()}
          {renderGridItems()}
        </div>
      </div>

      <div className="legend">
        <h3>Wall Items:</h3>
        <div className="legend-items">
          {wallItems.map(item => (
            <div
              key={item.id}
              className={`legend-item ${selectedItem === item.id ? 'selected' : ''}`}
              onClick={() => setSelectedItem(item.id)}
              style={{ borderColor: item.color }}
            >
              <span className="legend-icon">{item.icon}</span>
              <span className="legend-name">{item.name}</span>
              <span className="legend-position">
                {layouts[item.id]?.gridColumn} × {layouts[item.id]?.gridRow}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="editor-actions">
        <button
          className="save-button"
          onClick={saveLayout}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Layout'}
        </button>

        <button
          className="reset-button"
          onClick={resetToDefault}
        >
          Reset to Default
        </button>
      </div>

      {message && (
        <div className={`editor-message ${message.startsWith('✓') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="editor-notes">
        <h3>📝 How to Use:</h3>
        <ul>
          <li><strong>Move:</strong> Click and drag any item to a new position on the grid</li>
          <li><strong>Resize:</strong> Click an item to select it, then use the arrow buttons to resize</li>
          <li><strong>Grid:</strong> 12 columns × 8 rows (numbers show column,row position)</li>
          <li><strong>Save:</strong> Click "Save Layout" to apply changes to the homepage</li>
          <li><strong>Reset:</strong> Click "Reset to Default" to restore the original layout</li>
        </ul>
      </div>
    </div>
  );
};

export default WallLayoutEditor;
